import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { Button, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { api } from '../services';

const baseUrlCRM = process.env.REACT_APP_CRMFILE_SERVER;

export const UploadFiles = () => {
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();
  const [files, setFiles] = useState({});
  const [requestFiles, setRequestFiles] = useState([]); // parsed files from qRequest.Files
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [profile, setProfile] = useState(null);
  const [qRequest, setQRequest] = useState(null); // whole request object available across the page
  const key = localStorage.getItem('key');
  const baseURL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const requestIdFromStorage = localStorage.getItem('requestid') || '';

  const handleBack = () => {
    navigate(-1);
  };

  // بررسی وجود key و دریافت اطلاعات پروفایل
  useEffect(() => {
    if (!key) {
      navigate('/login');
      return;
    }

    const fetchProfile = async () => {
      try {
        const response = await api.get(`Home/GetProfileInfo?SesID=${key}&OID=1`);
        let data = response.data;

        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (error) {
            console.error('Invalid JSON string:', data);
            setModalMessage('خطا در دریافت اطلاعات پروفایل');
            setIsError(true);
            setShowModal(true);
            return;
          }
        }

        if (Array.isArray(data) && data.length > 0) {
          setProfile(data[0]);
          localStorage.setItem('patientID', data[0].PatientID);
          if (!data[0].Melicode) {
            navigate('/edirPro');
          }
        } else {
          setModalMessage('اطلاعات پروفایل یافت نشد');
          setIsError(true);
          setShowModal(true);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        setModalMessage('خطا در ارتباط با سرور برای دریافت پروفایل');
        setIsError(true);
        setShowModal(true);
      }
    };

    fetchProfile();
  }, [key, navigate]);

  // load request from server (GetRequest) and expose it to the page (qRequest)
  useEffect(() => {
    const loadRequest = async () => {
      const ccid = requestIdFromStorage;
      if (!ccid) return;
      try {
        const data = await getRequest();
        // normalize response shape - could be array or object
        const reqObj = Array.isArray(data) ? data[0] : data;
        if (reqObj) {
          setQRequest(reqObj);
          // fill the question textarea with RequestDesc if exists
          const desc = reqObj.RequestDesc ?? reqObj.requestDesc ?? reqObj.Description ?? '';
          if (desc) setValue('question', desc);

          // parse Files field if present (files separated by $$)
          const filesField = reqObj.Files ?? reqObj.files ?? reqObj.FileList ?? '';
          if (filesField) {
            const parsed = String(filesField)
              .split('$$')
              .map(f => f && f.trim())
              .filter(Boolean)
              .map((name) => {
                // construct URL for file - adjust path if your server stores elsewhere
                const url = `${baseUrlCRM.replace(/\/$/, '')}/DM/${encodeURIComponent(name)}`;
                const ext = (name.split('.').pop() || '').toLowerCase();
                const isImage = ['png', 'jpg', 'jpeg','pdf'].includes(ext);
                const isPdf = ext === 'pdf';
                return { name, url, ext, isImage, isPdf };
              });
            setRequestFiles(parsed);
          } else {
            setRequestFiles([]);
          }
        }
      } catch (err) {
        console.error('Failed to load request:', err);
      }
    };

    loadRequest();
  // run once on mount and when request id changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestIdFromStorage]);

  // make qRequest available on window for quick debugging (optional)
  useEffect(() => { if (typeof window !== 'undefined') window.__qRequest = qRequest; }, [qRequest]);

  // به‌روزرسانی فایل‌ها در فرم هنگام تغییر
  useEffect(() => {
    setValue('files', files);
  }, [files, setValue]);

  const validateFileExtension = (file) => {
    const validExtensions = ['.png', '.jpg', '.jpeg','.pdf'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    return validExtensions.includes(fileExtension);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    onDrop: (acceptedFiles) => {
      const invalidFiles = acceptedFiles.filter(file => !validateFileExtension(file));
      if (invalidFiles.length > 0) {
        const invalidFileNames = invalidFiles.map(file => file.name).join(', ');
        setModalMessage(`فرمت فایل‌های زیر نامعتبر است: ${invalidFileNames}\nفقط فایل‌های png، jpg، jpeg,pdf مجاز هستند.`);
        setIsError(true);
        setShowModal(true);
        return;
      }
      setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      setValue('files', [...files, ...acceptedFiles]);
    },
    multiple: true,
  });

  const searchPatient = async (mobile) => {
    try {
      const response = await fetch(
        `${baseUrlCRM}api/doapi?apikey=${process.env.REACT_APP_API_KEY}&func=SearchBimar&Mobile=${mobile}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (error) {
        console.error('Invalid JSON response from SearchBimar:', text);
        throw new Error('پاسخ سرور نامعتبر است');
      }

      return data;
    } catch (error) {
      console.error('Error searching patient:', error);
      throw error;
    }
  };

  const getRequest = async () => {
    const ccid=localStorage.getItem('requestid') || '';
    try {
      const response = await fetch(
        `${baseUrlCRM}api/doapi?apikey=${process.env.REACT_APP_API_KEY}&func=GetRequest&ccid=${ccid}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (error) {
        console.error('Invalid JSON response from SearchBimar:', text);
        throw new Error('پاسخ سرور نامعتبر است');
      }

      return data;
    } catch (error) {
      console.error('Error searching patient:', error);
      throw error;
    }
  };

    const GetVaziatByCode = async (code) => {
      try {
        const response = await fetch(
          `${baseUrlCRM}api/doapi?apikey=${process.env.REACT_APP_API_KEY}&func=GetVaziatByCode&vcode=${code}`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
            },
          }
        );
  
        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (error) {
          console.error('Invalid JSON response from SearchBimar:', text);
          throw new Error('پاسخ سرور نامعتبر است');
        }
  
        return data;
      } catch (error) {
        console.error('Error searching patient:', error);
        throw error;
      }
    };
    
  const addFileToRequest = async (requestId, fileNames, question = '') => {
    try {
      // دریافت وضعیت پیش‌فرض (اگر نیاز است)
      const VaziatData = await GetVaziatByCode('103');
      let vaziatccid = 0;
      if (Array.isArray(VaziatData) && VaziatData.length > 0) {
        vaziatccid = VaziatData[0].ccid;
      }

      // اگر قبلاً فایل‌هایی در درخواست وجود دارد، آنها را به ابتدای رشته اضافه کن
      let existingFilesRaw = '';
      try {
        const req = qRequest || (await getRequest());
        existingFilesRaw = req?.Files ?? req?.files ?? req?.FileList ?? '';
      } catch (e) {
        existingFilesRaw = '';
      }
      let finalFileNames = String(fileNames || '').trim();
      if (existingFilesRaw && existingFilesRaw.trim()) {
        // اگر هر دو مقدار موجود باشند، با جداکننده $$ ترکیب کن
        finalFileNames = existingFilesRaw.trim() + (finalFileNames ? `$$${finalFileNames}` : '');
      }

      // ساخت پارامترها به صورت دقیق مطابق با فرمت درخواستی
      const params = new URLSearchParams();
      params.append('apikey', process.env.REACT_APP_API_KEY);
      params.append('func', 'AddFileToRequest');
      params.append('userid', '36400');
      params.append('ccid', requestId);
      params.append('files', finalFileNames);
      params.append('Vaziat', vaziatccid);
      // اضافه کردن متن سوال/توضیحات به پارامترها
      if (question) params.append('RequestDesc', question);

      console.log('AddFileToRequest Parameters:', {
        apikey: process.env.REACT_APP_API_KEY,
        func: 'AddFileToRequest',
        userid: '36400',
        ccid: requestId,
        files: finalFileNames,
        RequestDesc: question,
        Vaziat: vaziatccid
      });

      const response = await fetch(`${baseUrlCRM}/api/Doupdateapi?${params.toString()}`, {
        method: 'GET',
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (error) {
        console.error('Invalid JSON response from AddFileToRequest:', text);
        throw new Error('پاسخ سرور نامعتبر است');
      }

      if (!data) {
        throw new Error('ثبت فایل در درخواست ناموفق بود');
      }
      return data;
    } catch (error) {
      console.error('Error adding file to request:', error);
      throw error;
    }
  };

  const uploadHandler = async (data) => {
    const filesArray = Object.values(files);
    if (!profile) {
      setModalMessage('اطلاعات پروفایل موجود نیست');
      setIsError(true);
      setShowModal(true);
      return;
    }
  
    // بررسی فرمت فایل‌ها
    const invalidFiles = filesArray.filter(file => !validateFileExtension(file));
    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(file => file.name).join(', ');
      setModalMessage(`فرمت فایل‌های زیر نامعتبر است: ${invalidFileNames}\nفقط فایل‌های png، jpg، jpeg,pdf مجاز هستند.`);
      setIsError(true);
      setShowModal(true);
      return;
    }
  
    try {
      // جستجوی بیمار با شماره موبایل
      let patientID = localStorage.getItem('patientID');
      const searchResult = await searchPatient(profile.Mobile);
  
     
        patientID = searchResult[0].ccid;
     
  
      // ثبت درخواست
      const requestId = localStorage.getItem('requestid') || '';
  
      // آپلود فایل‌ها به API فعلی (UploadDocument)
      const formData = new FormData();
      filesArray.forEach((file) => formData.append('file', file));
      formData.append('PatientID', localStorage.getItem('patientID'));
  
      // آپلود فایل‌ها به API جدید (uploadfile)
      const newApiUrl = process.env.REACT_APP_CRMFILE_SERVER+'/uploader/uploadfile?apikey='+process.env.REACT_APP_API_KEY;
      
      // آرایه برای ذخیره نام‌های فایل‌های آپلود شده
      const uploadedFileNamesArray = [];
      
      // آپلود هر فایل به صورت جداگانه
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        if (file) {
          try {
            // بررسی مجدد فرمت فایل قبل از آپلود
            if (!validateFileExtension(file)) {
              throw new Error(`فرمت فایل "${file.name}" نامعتبر است. فقط فایل‌های png، jpg، jpeg,pdf مجاز هستند.`);
            }

            // ایجاد FormData برای هر فایل
            const formData = new FormData();
            formData.append('file', file);
            
            console.log('Uploading file:', {
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              extension: file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
            });
            
            // ایجاد درخواست POST برای هر فایل
            const response = await fetch(newApiUrl, {
              method: 'POST',
              body: formData
            });

            const responseText = await response.text();
            console.log('Server response:', responseText);

            if (response.ok) {
              try {
                const responseData = JSON.parse(responseText);
                if (responseData.error === 0 && responseData.error_msg) {
                  // ذخیره نام فایل در آرایه
                  uploadedFileNamesArray.push(responseData.error_msg);
                } else {
                  throw new Error(`پاسخ API جدید نامعتبر است برای فایل ${i + 1}: ${responseData.error_mdg || 'خطای نامشخص'}`);
                }
              } catch (error) {
                console.error(`پاسخ uploadfile JSON نیست یا ساختار نادرست است برای فایل ${i + 1}:`, responseText);
                throw new Error(`خطا در پردازش پاسخ API جدید برای فایل ${i + 1}`);
              }
            } else {
              throw new Error(`خطا در آپلود فایل ${i + 1} به API جدید: ${responseText}`);
            }
          } catch (error) {
            console.error(`Error processing file ${i + 1}:`, error);
            throw error;
          }
        }
      }

      // ایجاد رشته نام‌های فایل با فرمت مورد نظر
      const formattedFileNames = uploadedFileNamesArray.length > 0 
        ? uploadedFileNamesArray.join('$$')
        : '';

      console.log('Formatted File Names:', formattedFileNames);

      // فراخوانی AddFileToRequest با تمام نام‌های فایل و ارسال متن سوال (question)
      const questionText = data.question || '';
      if (formattedFileNames) {
        await addFileToRequest(requestId, formattedFileNames, questionText);
      } else {
        throw new Error('هیچ فایلی برای آپلود وجود ندارد');
      }

      setModalMessage('فایل‌ها، درخواست و اطلاعات فایل‌ها با موفقیت ثبت شدند');
      setIsError(false);
      setShowModal(true);
      setFiles([]);
      setValue('question', '');
      setValue('description', '');
      setValue('files', []);
      // بعد از اتمام عملیات به صفحه DoctorOnline هدایت کن
      navigate('/DoctorOnline');
    } catch (error) {
      console.error('Error uploading files or processing request:', error);
      setModalMessage('خطا در ارتباط با سرور');
      setIsError(true);
      setShowModal(true);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setModalMessage('');
    setIsError(false);
  };

  // helper to render request files gallery / links
  const renderRequestFiles = () => {
    if (!requestFiles || requestFiles.length === 0) return null;
    return (
      <div style={{ marginTop: 12 }}>
        <label className="mb-1">فایل‌های موجود در درخواست</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {requestFiles.map((f, idx) => (
            <div key={idx} style={{ width: 110, textAlign: 'center' }}>
              {f.isImage ? (
                <a href={f.url} target="_blank" rel="noreferrer" download>
                  <img src={f.url} alt={f.name} style={{ width: 110, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #e9ecef' }} />
                </a>
              ) : f.isPdf ? (
                <a href={f.url} target="_blank" rel="noreferrer" download style={{ display: 'inline-block', textDecoration: 'none', color: '#000' }}>
                  <div style={{ width: 110, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px solid #e9ecef', background: '#fff' }}>
                    <FontAwesomeIcon icon={faFilePdf} size="lg" color="#d9534f" />
                  </div>
                </a>
              ) : (
                <a href={f.url} target="_blank" rel="noreferrer" download style={{ display: 'inline-block', textDecoration: 'none', color: '#000' }}>
                  <div style={{ width: 110, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px solid #e9ecef', background: '#fff' }}>
                    <span style={{ fontSize: 12 }}>{f.name}</span>
                  </div>
                </a>
              )}
              <div style={{ fontSize: 12, marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const removeFile = (index) => {
  setFiles((prev) => {
    const updated = { ...prev };
    delete updated[index];
    return updated;
  });

  // پاک کردن انتخاب input
  const input = document.getElementById(`file-${index}`);
  if (input) input.value = "";
};

  return (
    <>
      <section className="container mb-5 mt-xl-0 mt-4">
        <div className="row mb-4 mt-2">
          <div className="col-lg-12 path">
            <ul>
              <li>
                <Link className="text-decoration-none" to="/">
                  صفحه اصلی
                </Link>

                <FontAwesomeIcon icon={faAngleLeft} className="mr-2" />
              </li>
              {location.state?.from && (
                <li>
                  <span style={{ cursor: 'pointer' }} onClick={handleBack} className="text-primary">
                    {location.state.from}
                  </span>
                  <FontAwesomeIcon icon={faAngleLeft} className="mr-2" />
                </li>
              )}
              <li>
                <span>پرسش و پاسخ</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="row gap-3 justify-content-center">
          <div className="col-lg-6 card p-3 shadow rounded-3 mx-2">
            <h3 className="mb-4 text-center">ارسال سوال به پزشک</h3>
            <form onSubmit={handleSubmit(uploadHandler)}>
              <div className="form-group mb-3">
                <label htmlFor="question" className="mb-1">
                  شرح مشکل و توضیحات (اجباری)
                </label>
                <textarea
    id="question"
    className={`form-control ${errors.question ? 'is-invalid' : ''}`}
    rows="7"
    placeholder="سوال خود را وارد کنید"
    style={{ textAlign: 'right' }}
    maxLength={200}
    {...register('question', { 
      required: 'سوال الزامی است',
      maxLength: {
        value: 200,
        message: 'حداکثر 200 کاراکتر مجاز است'
      }
    })}
  />
                {errors.question && (
                  <div className="invalid-feedback">{errors.question.message}</div>
                )}
                  {/* شمارنده کاراکتر */}
  <div style={{ fontSize: 12, textAlign: 'left', color: '#6c757d' }}>
    {watch('question')?.length || 0} / 200
  </div>
              </div>

            

              <div className="form-group mb-3">
                <label htmlFor="files" className="mb-1">
                  فایل‌ها (اجباری)(فرمت های قابل بارگذاری png,jpg,jpeg,pdf)
                </label>
                <div className="row">
                  {[1, 2, 3, 4, 5, 6].map((index) => (
                    <div key={index} className="col-md-4 mb-3">
                      <div className="upload-container h-100">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="upload-number">{index}</span>
                          <h6 className="mb-0">آپلود فایل {index}</h6>
                        </div>
                        <div className="custom-file-upload">
                          <input
                            type="file"
                            id={`file-${index}`}
                            className="form-control"
                            placeholder="فایل خود را انتخاب کنید"
                            accept=".png,.jpg,.jpeg,.PNG,.JPG,.JPEG,.pdf,.PDF"
                            onChange={(e) => {
  const file = e.target.files[0];
  if (file) {
    if (!validateFileExtension(file)) {
      setModalMessage(`فرمت فایل "${file.name}" نامعتبر است. فقط فایل‌های png، jpg، jpeg,pdf مجاز هستند.`);
      setIsError(true);
      setShowModal(true);
      e.target.value = '';
      return;
    }

    setFiles((prev) => ({
      ...prev,
      [index]: file
    }));

    setValue('files', { ...files, [index]: file });
  }
}}
                          />
                            {/* دکمه حذف فایل */}
  {files[index] && (
    <button
      type="button"
      onClick={() => removeFile(index)}
      style={{
        position: 'absolute',
        top: '5px',
        right: '5px',
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        background: '#dc3545',
        color: 'white',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        lineHeight: '20px',
        textAlign: 'center',
      }}
    >
      ×
    </button>
  )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* نمایش فایل‌های فعلی درخواست (اگر وجود داشته باشند) */}
                {renderRequestFiles()}
                <style>
                  {`
                    .upload-container {
                      background: #f8f9fa;
                      padding: 10px;
                      border-radius: 6px;
                      border: 1px solid #e9ecef;
                      transition: all 0.3s ease;
                      max-width: 200px;
                      margin: 0 auto;
                    }
                    .upload-container:hover {
                      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                      transform: translateY(-2px);
                    }
                    .upload-number {
                      background: #007bff;
                      color: white;
                      width: 20px;
                      height: 20px;
                      border-radius: 50%;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: 12px;
                    }
                    .upload-container h6 {
                      font-size: 14px;
                      margin: 0;
                    }
                    .custom-file-upload {
                      position: relative;
                    }
                    .custom-file-upload input[type="file"] {
                      border: 1px dashed #dee2e6;
                      padding: 6px;
                      border-radius: 4px;
                      background: white;
                      width: 100%;
                      font-size: 12px;
                    }
                    .custom-file-upload input[type="file"]:hover {
                      border-color: #007bff;
                    }
                    @media (max-width: 768px) {
                      .col-md-4 {
                        width: 50%;
                      }
                      .upload-container {
                        max-width: 180px;
                      }
                    }
                    @media (max-width: 576px) {
                      .col-md-4 {
                        width: 100%;
                      }
                      .upload-container {
                        max-width: 160px;
                      }
                    }
                  `}
                </style>
              </div>

              <div className="d-flex justify-content-end">
                <Button type="submit" className="mt-3 btn btn-primary" style={{ width: 'max-content' }}>
                  ارسال سوال
                </Button>
              </div>
            </form>
          </div>

          <div className="col-lg-5 card p-3 shadow rounded-3 mx-2">
            <img
              src={'/Img/faq.png'}
              className="wallet-image w-100 h-100"
              alt="wallet"
              style={{ objectFit: 'cover' }}
            />
          </div>
        </div>

        <Modal show={showModal} onHide={handleModalClose} centered>
          <Modal.Header>
            <Modal.Title>{isError ? 'خطا' : 'موفقیت'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{modalMessage}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleModalClose}>
              بستن
            </Button>
          </Modal.Footer>
        </Modal>
      </section>
    </>
  );
};